import { __assign, __rest } from "tslib";
import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import isPlainObject from 'lodash/isPlainObject';
import './RadioButtonGroup.scss';
var RadioButtonGroupAlignmentTypes = {
    INLINE: 'inline',
    STACKED: 'stacked'
};
var RadioButtonGroup = function (_a) {
    var align = _a.align, className = _a.className, errorMessage = _a.errorMessage, isDisabled = _a.isDisabled, isLabeled = _a.isLabeled, isRequired = _a.isRequired, label = _a.label, name = _a.name, onChange = _a.onChange, options = _a.options, value = _a.value, props = __rest(_a, ["align", "className", "errorMessage", "isDisabled", "isLabeled", "isRequired", "label", "name", "onChange", "options", "value"]);
    var containerClassName = classnames('RadioButtonGroup', "RadioButtonGroup--".concat(align), className);
    var checkmark = (React.createElement("div", { className: 'RadioButtonGroup__checkmark' },
        React.createElement("div", { "aria-hidden": 'true', className: 'RadioButtonGroup__checkmarkOuter', role: 'presentation' }),
        React.createElement("div", { "aria-hidden": 'true', className: 'RadioButtonGroup__checkmarkInner', role: 'presentation' })));
    var formattedOptions = options.map(function (option) {
        return isPlainObject(option)
            ? option
            : { label: option, value: option };
    });
    return (React.createElement("div", __assign({}, props, { "aria-labelledby": name, className: containerClassName, role: 'radiogroup' }),
        label && (React.createElement("div", { className: 'RadioButtonGroup__label' },
            isRequired ? React.createElement("span", { "aria-hidden": 'true' }, "* ") : null,
            label)),
        React.createElement("div", { className: 'RadioButtonGroup__optionList' }, formattedOptions.map(function (option, index) {
            var isSelected = value === option.value;
            var radioButtonGroupItemClass = classnames('RadioButtonGroup__item', {
                'RadioButtonGroup__item--isSelected': isSelected,
                'RadioButtonGroup__item--isDisabled': isDisabled
            });
            return (React.createElement("label", { className: radioButtonGroupItemClass, key: option.value },
                React.createElement("input", { "aria-checked": isSelected, checked: isSelected, className: 'RadioButtonGroup__nativeCheckmark', "data-testid": "radio-".concat(option.value), disabled: isDisabled, name: name, onChange: function () { return onChange(option.value); }, required: isRequired && index === 0, type: 'radio', value: option.value }),
                React.createElement("span", { className: 'RadioButtonGroup__checkmarkAndLabel' },
                    checkmark,
                    isLabeled && (React.createElement("span", { className: 'RadioButtonGroup__itemLabel' }, option.label)))));
        })),
        errorMessage && (React.createElement("div", { className: 'RadioButtonGroup__errorMessage', role: 'alert' }, errorMessage))));
};
RadioButtonGroup.defaultProps = {
    align: RadioButtonGroupAlignmentTypes.INLINE,
    isDisabled: false,
    isLabeled: true
};
RadioButtonGroup.propTypes = {
    /** The alignment of the radio buttons in the group */
    align: PropTypes.oneOf(Object.values(RadioButtonGroupAlignmentTypes)),
    /** Class name to apply to the container div */
    className: PropTypes.string,
    /** Error message to display  */
    errorMessage: PropTypes.node,
    /** True if the radio button group is disabled */
    isDisabled: PropTypes.bool,
    /** True if the radio buttons should have labels displayed visually */
    isLabeled: PropTypes.bool,
    /** Label for the radio button group */
    label: PropTypes.node,
    /**
     * Name for the group of radio buttons. If used in a form  context, the name will
     * be the name of the attribute provided to the backend
     */
    name: PropTypes.string.isRequired,
    onBlur: PropTypes.func,
    /**
     * Function called when a radio button is clicked, called with the radio's
     * value
     */
    onChange: PropTypes.func.isRequired,
    /**
     * Data describing the options to render. An array of values or an array of objects with label
     * and value keys.
     */
    options: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.shape({
            label: PropTypes.node,
            value: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
                PropTypes.bool,
            ])
        })),
        PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool])),
    ]).isRequired,
    /**
     * The currently selected value.
     */
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
    ])
};
export default RadioButtonGroup;
//# sourceMappingURL=RadioButtonGroup.js.map